import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useQuery } from '@apollo/client';
import { TableBody, TableHead, TableRow } from '@mui/material';
import { IconTime } from '../../../../../../../components';
import { CircularProgress } from '../../../../../../../components/circular-progress';
import { useInstallationContext } from '../../../../../../../context/installation';
import {
  BucketInfo,
  SeasonEntries,
  SeasonPricesQuery,
  SeasonPricesQueryVariables,
} from '../../../../../../../data-access/gql-types/graphql';
import { SEASON_PRICES } from '../../../../../../../data-access/queries/meter';
import { HeatmapCell } from '../../components/heat-map-cell';
import { HeatmapLegend } from '../../components/heatmap-legend';
import { TableCellStyled } from '../../components/table-cell';
import { TablePrice } from '../../components/table-price';
import { formatHour, getHeatmapColor } from '../../utils';

type TableRow = {
  hours: string;
  summerWorkPrice: number;
  summerOffWorkPrice: number;
  winterWorkPrice: number;
  winterOffWorkPrice: number;
};

export const ChartG13s: React.FC = () => {
  const { t } = useTranslation('channel-details');
  const { t: tnf } = useTranslation('configuration');
  const { skipLavvaFetch, selectedInstallationId } = useInstallationContext();
  const { data, loading } = useQuery<SeasonPricesQuery, SeasonPricesQueryVariables>(SEASON_PRICES, {
    variables: { installationId: selectedInstallationId },
    skip: skipLavvaFetch,
  });

  const seasonsPrices = useMemo(() => {
    const prices = data?.seasonPrices;
    if (!prices) return [];

    const { summer, winter } = prices;

    return Array.from({ length: 24 }).map((_, i) => {
      return {
        hours: formatHour(summer.workingDayEntries[i].startingHour),
        summerWorkPrice: summer.workingDayEntries[i].priceInkWh,
        summerOffWorkPrice: summer.offFromWorkDayEntries[i].priceInkWh,
        winterWorkPrice: winter.workingDayEntries[i].priceInkWh,
        winterOffWorkPrice: winter.offFromWorkDayEntries[i].priceInkWh,
      };
    }) as TableRow[];
  }, [data?.seasonPrices]);

  const formatDate = (date: string) => format(new Date(date), 'dd.MM.yyyy');

  const renderValueCell = (val: number, index: number) => {
    const bucketInfos = data?.seasonPrices?.bucketInfos as BucketInfo[] | null;
    const color = getHeatmapColor(val, bucketInfos);

    return (
      <HeatmapCell
        value={val}
        color={color}
        orientation="vertical"
        roundTop={index === 0}
        roundBottom={index === seasonsPrices.length - 1}
      />
    );
  };

  const renderHeaderCell = (title: string) => (
    <TableCellStyled align="center" style={{ fontWeight: 600 }}>
      {title === 'hour' ? <IconTime /> : t(`optimizer.configuration.tariff.${title}`)}
    </TableCellStyled>
  );

  const renderSubHeaderCell = (translateKey: string, season: SeasonEntries) => (
    <TableCellStyled
      align="center"
      colSpan={2}
      style={{ fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'pre-wrap' }}
    >
      {`${t(`optimizer.configuration.tariff.${translateKey}`)}\r\n`}
      <span style={{ fontSize: 12 }}>{`(${formatDate(season.from)} - ${formatDate(season.to)})`}</span>
    </TableCellStyled>
  );

  const summer = data?.seasonPrices?.summer;
  const winter = data?.seasonPrices?.winter;

  if (loading) return <CircularProgress />;
  if (!seasonsPrices.length || !summer || !winter) return null;

  return (
    <>
      <HeatmapLegend subTitle={tnf('tariffPricesInfo')} />
      <TablePrice>
        <TableHead>
          <TableRow>
            <TableCellStyled colSpan={1}></TableCellStyled>
            {renderSubHeaderCell('winterTime', winter)}
            {renderSubHeaderCell('summerTime', summer)}
          </TableRow>
          <TableRow>
            {renderHeaderCell('hour')}
            {renderHeaderCell('workingDays')}
            {renderHeaderCell('offWorkingDays')}
            {renderHeaderCell('workingDays')}
            {renderHeaderCell('offWorkingDays')}
          </TableRow>
        </TableHead>
        <TableBody>
          {seasonsPrices.map((row, index) => (
            <TableRow key={row.hours}>
              <TableCellStyled align="center">{row.hours}</TableCellStyled>
              {renderValueCell(row.winterWorkPrice, index)}
              {renderValueCell(row.winterOffWorkPrice, index)}
              {renderValueCell(row.summerWorkPrice, index)}
              {renderValueCell(row.summerOffWorkPrice, index)}
            </TableRow>
          ))}
        </TableBody>
      </TablePrice>
    </>
  );
};
